/**
 * @fileoverview Experience.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React, { useState, useMemo, useRef, useEffect } from 'react'
import TopImageBanner from '../components/tours/TopImageBanner'
import ExperienceCard from '../components/experiences/ExperienceCard'
import ExperienceModal from '../components/experiences/ExperienceModal'
import { Categories, ExperiencesData } from '../assets/Assets'
import { apiFetch } from '../utils/api'
import SEO from '../components/ui/SEO'
import { useTranslation } from 'react-i18next'

// Pre-format static Assets.js data so it matches the API shape exactly.
// This is the instant first-paint content - zero network requests needed.
const staticExperiences = ExperiencesData.map(exp => ({
  id: exp.id,
  name: exp.name,
  description: exp.description || '',
  categories: exp.categories || [],
  image: exp.image || { h: '', l: '' },
})).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))

function SkeletonExperienceCard() {
  return (
    <div className="w-full flex flex-col md:flex-row gap-6 md:gap-10 items-center animate-pulse">
      <div className="w-full md:w-5/12 h-64 md:h-80 bg-gray-200 rounded-3xl overflow-hidden shrink-0"></div>
      <div className="w-full md:w-7/12 flex flex-col items-start text-left gap-4 p-4 md:p-0">
        <div className="flex gap-2 w-full">
          <div className="h-4 w-20 bg-gray-200 rounded-full"></div>
          <div className="h-4 w-20 bg-gray-200 rounded-full"></div>
        </div>
        <div className="h-8 w-3/4 bg-gray-200 rounded"></div>
        <div className="h-4 w-full bg-gray-200 rounded mt-2"></div>
        <div className="h-4 w-full bg-gray-200 rounded"></div>
        <div className="h-4 w-5/6 bg-gray-200 rounded"></div>
        <div className="h-10 w-32 bg-gray-200 rounded-full mt-4"></div>
      </div>
    </div>
  )
}

function Experience() {
  const { t } = useTranslation();
  // Initialise with static data → renders instantly, no blank screen or spinner
  const [experiences, setExperiences] = useState(staticExperiences);
  // loading = true only means "API refresh in progress" - shown as a subtle badge
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const itemsPerPage = 12;
  const listRef = useRef(null);

  useEffect(() => {
    apiFetch('/experiences')
      .then(res => {
        if (res && res.experiences && res.experiences.length > 0) {
          // Silently replace static data with up-to-date API data
          const formatted = res.experiences.map(exp => ({
            id: exp._id,
            name: exp.title,
            description: exp.description || '',
            categories: exp.categories || [],
            image: (exp.image && typeof exp.image === 'object' && (exp.image.l || exp.image.h))
              ? { l: exp.image.l || exp.image.h, h: exp.image.h || exp.image.l }
              : { l: exp.image || 'https://images.unsplash.com/photo-1546708973-b339540b5162?w=800', h: exp.image || 'https://images.unsplash.com/photo-1546708973-b339540b5162?w=800' }
          })).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
          setExperiences(formatted);
        }
        // If API returns empty or backend is down, keep showing the static Assets.js data silently
      })
      .catch(() => {
        // Network error - static Assets.js data remains displayed, no error shown
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const filteredExperiences = useMemo(() => {
    let filtered = experiences;
    if (selectedCategory !== 'All') {
      filtered = experiences.filter(exp =>
        exp.categories && exp.categories.some(cat => cat.trim().toLowerCase() === selectedCategory.trim().toLowerCase())
      );
    }
    return filtered;
  }, [experiences, selectedCategory]);

  const totalPages = Math.ceil(filteredExperiences.length / itemsPerPage);

  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredExperiences.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredExperiences, currentPage]);

  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (listRef.current) {
      listRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const experiencesSchema = useMemo(() => {
    if (experiences.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Sri Lanka Travel Experiences",
      "description": "A curated list of authentic experiences and things to do in Sri Lanka by KITH Travels.",
      "numberOfItems": experiences.length,
      "itemListElement": experiences.map((exp, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "item": {
          "@type": "TouristAttraction",
          "name": exp.name,
          "description": exp.description,
          "image": exp.image.h,
          "url": `${window.location.origin}/experience`
        }
      }))
    };
  }, [experiences]);

  return (
    <div className='flex flex-col w-full min-h-screen pt-24 md:pt-32'>
      <SEO
        title="Experiences"
        description="Discover unforgettable experiences across Sri Lanka - from the Great Elephant Gathering at Minneriya and blue whale encounters off Mirissa to ancient rock fortress climbs, scenic train journeys, and hands-on cooking classes. Find your perfect adventure."
        keywords="Sri Lanka experiences, things to do Sri Lanka, elephant gathering Minneriya, whale watching Mirissa, Sigiriya climb, Ella train journey, Sri Lankan cooking class, Yala safari, Galle Fort, surfing Weligama"
        schema={experiencesSchema}
      />

      {/* Hero Section */}
      <div className='px-[5%] md:px-[10%]'>
        <div className='relative w-full h-[50vh] md:h-[60vh]'>
          <TopImageBanner title={t('experience.hero_title')} />
        </div>
      </div>

      <div className='flex flex-col w-full gap-8 px-[5%] md:px-[10%] py-16 items-start'>

        {/* Filter Section */}
        <div className='flex flex-col gap-3 w-full'>
          <div className='flex items-center gap-3'>
            <span className='text-xs font-bold text-gray-400 uppercase tracking-widest'>{t('experience.filter_cat')}</span>

            {/* Subtle "Loading latest" badge - visible only while API is still fetching */}
            {loading && (
              <span className='flex items-center gap-1.5 text-[10px] font-semibold text-KITH-primary/70 border border-KITH-primary/20 px-2.5 py-0.5 rounded-full'>
                <span className='w-1.5 h-1.5 rounded-full bg-KITH-primary animate-pulse'></span>
                {t('experience.loading')}
              </span>
            )}
          </div>

          <div className='flex flex-wrap gap-2'>
            {['All', ...Categories].map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`px-5 py-2 rounded-full whitespace-nowrap font-bold text-sm transition-all border ${
                  selectedCategory === cat
                    ? 'bg-KITH-primary text-white border-KITH-primary shadow-sm'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-KITH-primary hover:text-KITH-primary'
                }`}
              >
                {cat === 'All' ? t('tour_filter.all') : t(`categories.${cat}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Experience List - always visible, content never blocked by a spinner */}
        <div ref={listRef} className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 w-full mt-6 min-h-[300px]'>
          {loading ? (
             <>
               <SkeletonExperienceCard />
               <SkeletonExperienceCard />
               <SkeletonExperienceCard />
             </>
          ) : currentItems.length > 0 ? (
            currentItems.map((exp, idx) => (
              <ExperienceCard 
                key={exp.id} 
                experience={exp} 
                index={idx} 
                onClick={setSelectedExperience}
              />
            ))
          ) : (
            <div className='py-20 text-center w-full'>
              <p className='text-gray-500 text-lg'>{t('experience.no_exp')}</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className='flex justify-center items-center gap-2 w-full mt-16'>
            <button
              onClick={() => handlePageChange(1)}
              className={`w-8 h-8 rounded-md font-bold transition-all text-xs ${currentPage === 1 ? 'bg-KITH-primary text-white shadow-md' : 'bg-white text-gray-400 border border-KITH-primary/20 hover:bg-gray-50'}`}
            >1</button>

            {currentPage > 3 && <span className='text-gray-400 px-1'>...</span>}

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => page !== 1 && page !== totalPages && Math.abs(page - currentPage) <= 1)
              .map(page => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-8 h-8 rounded-md font-bold transition-all text-xs ${currentPage === page ? 'bg-KITH-primary text-white shadow-md' : 'bg-white text-gray-400 border border-KITH-primary/20 hover:bg-gray-50'}`}
                >{page}</button>
              ))
            }

            {currentPage < totalPages - 2 && <span className='text-gray-400 px-1'>...</span>}

            <button
              onClick={() => handlePageChange(totalPages)}
              className={`w-8 h-8 rounded-md font-bold transition-all text-xs ${currentPage === totalPages ? 'bg-KITH-primary text-white shadow-md' : 'bg-white text-gray-400 border border-KITH-primary/20 hover:bg-gray-50'}`}
            >{totalPages}</button>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className='w-8 h-8 rounded-md border border-KITH-primary/20 text-gray-400 font-bold hover:bg-KITH-primary hover:text-white transition-all disabled:opacity-50 text-xs flex items-center justify-center'
            >
              <i className='fi fi-br-angle-right'></i>
            </button>
          </div>
        )}
      </div>

      {/* Experience Details Modal */}
      <ExperienceModal 
        experience={selectedExperience} 
        onClose={() => setSelectedExperience(null)} 
      />
    </div>
  )
}

export default Experience
